/**
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {connect} from 'react-redux';

import PropTypes from 'prop-types';

import {setEditContext} from '../actions/editing';
import {LayerRole, addLayerFeatures, removeLayer} from '../actions/layers';
import {getFeatureTemplate} from '../utils/EditingUtils';
import LocaleUtils from '../utils/LocaleUtils';
import AttributeForm from './AttributeForm';
import Icon from './Icon';

import './style/LinkFeatureForm.css';

class LinkFeatureForm extends React.Component {
    static propTypes = {
        action: PropTypes.string,
        addLayerFeatures: PropTypes.func,
        editConfig: PropTypes.object,
        editContextId: PropTypes.string,
        editing: PropTypes.object,
        feature: PropTypes.object,
        featureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        finished: PropTypes.func,
        hideDelete: PropTypes.bool,
        iface: PropTypes.object,
        map: PropTypes.object,
        mapPrefix: PropTypes.string,
        parentContextId: PropTypes.string,
        pickFilter: PropTypes.func,
        readOnly: PropTypes.bool,
        removeLayer: PropTypes.func,
        setEditContext: PropTypes.func,
        translations: PropTypes.object
    };
    state = {
        editContext: {},
        pickedFeatures: null,
        highlightedFeature: null
    };
    componentDidMount() {
        if (this.props.action === 'Edit') {
            if (this.props.feature) {
                this.props.setEditContext(this.props.editContextId, {
                    action: 'Pick', feature: this.props.feature, geomType: this.props.editConfig.geomType,
                    editConfig: this.props.editConfig, mapPrefix: this.props.mapPrefix,
                    parentContextId: this.props.parentContextId
                });
            } else {
                this.props.iface.getFeatureById(this.props.editConfig, this.props.featureId, this.props.map.projection, (result) => {
                    if (result) {
                        this.props.setEditContext(this.props.editContextId, {
                            action: 'Pick', feature: result, geomType: this.props.editConfig.geomType,
                            editConfig: this.props.editConfig, mapPrefix: this.props.mapPrefix,
                            parentContextId: this.props.parentContextId
                        });
                    }
                });
            }
        } else if (this.props.action === 'Create' || this.props.action === 'PickGeometry') {
            const featureSkel = {
                type: "Feature",
                properties: {},
                ...this.props.feature
            };
            const editAction = this.props.action === 'Create' ? 'Draw' : this.props.action;
            getFeatureTemplate(this.props.editConfig, featureSkel, this.props.iface, this.props.mapPrefix, this.props.map.projection, feature => {
                this.props.setEditContext(this.props.editContextId, {
                    action: editAction, geomType: this.props.editConfig.geomType, feature: feature,
                    editConfig: this.props.editConfig, mapPrefix: this.props.mapPrefix,
                    parentContextId: this.props.parentContextId
                });
            });
        } else if (this.props.action === 'Pick') {
            this.props.setEditContext(this.props.editContextId, {
                editConfig: this.props.editConfig, mapPrefix: this.props.mapPrefix,
                parentContextId: this.props.parentContextId
            });
        }
    }
    componentDidUpdate(prevProps) {
        // Handle drawPick
        const editContext = this.props.editing.contexts[this.props.editContextId];
        if (editContext && editContext.action === null && this.props.map.click && this.props.map.click !== prevProps.map.click) {
            this.childPickQuery(this.props.map.click.coordinate);
        }
    }
    render() {
        const editContext = this.props.editing.contexts[this.props.editContextId];
        if (!editContext) {
            return null;
        }

        if (editContext.action === null) {
            // Picking
            return (
                <div className="link-feature-form">
                    {!this.state.pickedFeatures ? (
                        <div className="link-feature-form-hint">
                            <span>{LocaleUtils.tr("linkfeatureform.picklayerhint", this.props.editConfig.layerTitle)}</span>
                        </div>
                    ) : (
                        <div className="link-feature-form-feature-list">
                            {this.state.pickedFeatures.map(feature => (
                                <div key={feature.id} onClick={() => this.pickFeatureSelected(feature)}
                                    onMouseOut={() => this.unhoverFeature(feature)} onMouseOver={() => this.hoverFeature(feature)}
                                >{feature.properties[this.props.editConfig.displayField] ?? feature.id}</div>
                            ))}
                        </div>
                    )}
                    {!editContext.changed && (<div className="link-feature-form-close">
                        <button className="button" onClick={this.close}>
                            <Icon icon="remove" />
                            {LocaleUtils.tr("common.close")}
                        </button>
                    </div>)}
                </div>
            );
        } else if (editContext.action === 'PickGeometry') {
            return (
                <div className="link-feature-form">
                    <div className="link-feature-form-hint">
                        <span>{LocaleUtils.tr("linkfeatureform.pickhint")}</span>
                    </div>
                    <div className="link-feature-form-close">
                        <button className="button" onClick={this.close}>
                            <Icon icon="remove" />
                            {LocaleUtils.tr("common.cancel")}
                        </button>
                    </div>
                </div>
            );
        } else if (editContext.feature) {
            const drawing = (editContext.action === 'Draw' && !editContext.feature.geometry && this.props.editConfig.geomType);

            return (
                <div className="link-feature-form">
                    {drawing ? (
                        <div className="link-feature-form-hint">
                            <span>{LocaleUtils.tr("linkfeatureform.drawhint")}</span>
                        </div>
                    ) : (
                        <AttributeForm
                            editContext={editContext} hideDelete={this.props.hideDelete}
                            iface={this.props.iface} nested onCommit={this.onCommit} onDiscard={this.onDiscard}
                            readOnly={this.props.readOnly} translations={this.props.translations}
                        />
                    )}
                    {!editContext.changed && (<div className="link-feature-form-close">
                        <button className="button" onClick={this.close}>
                            <Icon icon="remove" />
                            {editContext.action === 'Draw' ? LocaleUtils.tr("common.cancel") : LocaleUtils.tr("common.close")}
                        </button>
                    </div>)}
                </div>
            );
        } else {
            return null;
        }
    }
    childPickQuery = (coordinate) => {
        this.props.iface.getFeature(this.props.editConfig, coordinate, this.props.map.projection, this.props.map.scale, 96, (featureCollection) => {
            const features = featureCollection ? featureCollection.features : null;
            if (features && features.length === 1) {
                if (!this.props.pickFilter) {
                    this.props.finished(features[0]);
                } else {
                    const newFeature = this.props.pickFilter(features[0]);
                    if (newFeature) {
                        this.props.finished(newFeature);
                    }
                }
            } else {
                this.setState({pickedFeatures: features});
            }
        });
    };
    close = () => {
        const editContext = this.props.editing.contexts[this.props.editContextId];
        this.props.finished(editContext.action === 'Pick' ? editContext.feature : null);
    };
    hoverFeature = (feature) => {
        const layer = {
            id: this.props.editContextId + "-pick-selection",
            role: LayerRole.SELECTION
        };
        this.props.addLayerFeatures(layer, [feature], true);
        this.setState({highlightedFeature: feature.id});
    };
    unhoverFeature = (feature) => {
        if (this.state.highlightedFeature === feature.id) {
            this.props.removeLayer(this.props.editContextId + "-pick-selection");
            this.setState({highlightedFeature: null});
        }
    };
    pickFeatureSelected = (feature) => {
        this.unhoverFeature(feature);
        if (!this.props.pickFilter) {
            this.props.finished(feature);
        } else {
            const newFeature = this.props.pickFilter(feature);
            if (newFeature) {
                this.props.finished(newFeature);
            }
        }
    };
    onCommit = (result) => {
        this.props.finished(result);
        return true;
    };
    onDiscard = () => {
        this.props.finished(null);
        return true;
    };
}

export default connect((state) => ({
    editing: state.editing,
    map: state.map
}), {
    addLayerFeatures: addLayerFeatures,
    removeLayer: removeLayer,
    setEditContext: setEditContext
})(LinkFeatureForm);
